// ============================================================
// usePhotoEditor.js — editor state machine for the Photo Editor panel
// ------------------------------------------------------------
// Phase 1: photo selection, preset + strength, 14 adjustments, save/download.
// Phase 2: custom filters + favourites (Supabase, RLS), text layers, and a
//   batch save helper. All persistence is client-side via the anon client.
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { clientScopedFetch } from '../lib/authedFetch';
import { useViewingAs } from './useViewingAs';
import {
  DEFAULT_SETTINGS,
  resolvePresetSettings,
  isNeutral,
} from '../utils/filterEngine';
import { newTextLayer, MAX_TEXT_LAYERS } from '../utils/textStyles';
import { exportEdited } from '../utils/canvasExport';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
const MAX_FAVOURITES = 5;

// Phase 6: every persistence call in this hook ran through the anon client
// under RLS, scoped by the clientId PROP — which is the signed-in user, not
// the client an admin is viewing. So a filter saved, a favourite starred or an
// edited photo saved while viewing a client all landed on the ADMIN's rows.
// They now route through requireOwnerOrAdmin, which resolves the target.
//
// None of these send a client identifier: requireOwnerOrAdmin reads the body
// before the query string, so one would beat the viewing-as override.
async function editorFetch(path, options) {
  const res = await clientScopedFetch(`${BACKEND}${path}`, options);
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}

// Crop is stored as 0–1 fractions of the *original* image. Default = full
// frame (no-op). 'aspect' is the currently locked preset key (see CropTool).
const DEFAULT_CROP = { x: 0, y: 0, width: 1, height: 1 };
const CROP_EPS = 0.001;
const isCropFull = (c) =>
  Math.abs(c.x) < CROP_EPS && Math.abs(c.y) < CROP_EPS &&
  Math.abs(c.width - 1) < CROP_EPS && Math.abs(c.height - 1) < CROP_EPS;

export function usePhotoEditor({ clientId, showToast }) {
  // clientId stays a "signed in yet" guard only — the scope now comes from the
  // routes. viewingAsClientId is what makes the load below re-run on entering
  // or leaving a client view.
  const { viewingAsClientId } = useViewingAs();
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [activeFilter, setActiveFilter]   = useState('Normal'); // preset name | `custom:<id>`
  const [filterStrength, setFilterStrength] = useState(100);
  const [adjustments, setAdjustments]     = useState({ ...DEFAULT_SETTINGS });
  const [textLayers, setTextLayers]       = useState([]);
  const [activeTextLayerId, setActiveTextLayerId] = useState(null);
  const [crop, setCrop]                   = useState(DEFAULT_CROP);
  const [cropAspect, setCropAspect]       = useState('free');
  const [customFilters, setCustomFilters] = useState([]);
  const [favouriteFilters, setFavouriteFilters] = useState([]); // [{id,type,name}]
  const [isSaving, setIsSaving]           = useState(false);

  const toast = useCallback((m) => { if (showToast) showToast(m); }, [showToast]);

  const isDirty = useMemo(
    () => activeFilter !== 'Normal' || !isNeutral(adjustments) ||
      textLayers.length > 0 || !isCropFull(crop),
    [activeFilter, adjustments, textLayers, crop],
  );

  // ── Load custom filters + favourites for this client ──────────────────
  useEffect(() => {
    let alive = true;
    if (!clientId) return;
    (async () => {
      try {
        const d = await editorFetch('/api/photo-editor/prefs');
        if (!alive) return;
        setCustomFilters(d.customFilters || []);
        setFavouriteFilters(d.favouriteFilters || []);
      } catch (err) {
        console.error('[PhotoEditor] prefs load failed:', err.message);
      }
    })();
    return () => { alive = false; };
  }, [clientId, viewingAsClientId]);

  // ── Photo selection ──────────────────────────────────────────────────
  const loadPhoto = useCallback((photo) => {
    setSelectedPhoto(photo);
    setActiveFilter('Normal');
    setFilterStrength(100);
    setAdjustments({ ...DEFAULT_SETTINGS });
    setTextLayers([]);
    setActiveTextLayerId(null);
    setCrop(DEFAULT_CROP);
    setCropAspect('original');
  }, []);

  const clearPhoto = useCallback(() => {
    setSelectedPhoto(null);
    setActiveFilter('Normal');
    setFilterStrength(100);
    setAdjustments({ ...DEFAULT_SETTINGS });
    setTextLayers([]);
    setActiveTextLayerId(null);
    setCrop(DEFAULT_CROP);
    setCropAspect('original');
  }, []);

  const resetCrop = useCallback(() => {
    setCrop(DEFAULT_CROP);
    setCropAspect('original');
  }, []);

  // ── Filters ──────────────────────────────────────────────────────────
  const applyPreset = useCallback((name) => {
    setActiveFilter(name);
    setFilterStrength(100);
    setAdjustments(resolvePresetSettings(name, 1));
  }, []);

  const applyCustomFilter = useCallback((filter) => {
    setActiveFilter(`custom:${filter.id}`);
    setFilterStrength(100);
    setAdjustments({ ...DEFAULT_SETTINGS, ...(filter.settings || {}) });
  }, []);

  const setStrength = useCallback((value) => {
    const v = Math.max(0, Math.min(100, value));
    setFilterStrength(v);
    setActiveFilter((name) => {
      if (!name.startsWith('custom:')) setAdjustments(resolvePresetSettings(name, v / 100));
      return name;
    });
  }, []);

  // ── Adjustment sliders ───────────────────────────────────────────────
  const updateAdjustment = useCallback((key, value) => {
    setAdjustments((prev) => ({ ...prev, [key]: value }));
  }, []);
  const resetAdjustment = useCallback((key) => {
    setAdjustments((prev) => ({ ...prev, [key]: DEFAULT_SETTINGS[key] }));
  }, []);
  const resetAdjustments = useCallback(() => {
    setActiveFilter('Normal');
    setFilterStrength(100);
    setAdjustments({ ...DEFAULT_SETTINGS });
  }, []);

  // ── Text layers ──────────────────────────────────────────────────────
  const addTextLayer = useCallback(() => {
    setTextLayers((prev) => {
      if (prev.length >= MAX_TEXT_LAYERS) {
        toast(`Max ${MAX_TEXT_LAYERS} text layers`);
        return prev;
      }
      const layer = newTextLayer();
      setActiveTextLayerId(layer.id);
      return [...prev, layer];
    });
  }, [toast]);

  const updateTextLayer = useCallback((id, changes) => {
    setTextLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...changes } : l)));
  }, []);

  const removeTextLayer = useCallback((id) => {
    setTextLayers((prev) => prev.filter((l) => l.id !== id));
    setActiveTextLayerId((cur) => (cur === id ? null : cur));
  }, []);

  const selectTextLayer = useCallback((id) => setActiveTextLayerId(id), []);

  // ── Favourites (Supabase prefs upsert) ───────────────────────────────
  const persistFavourites = useCallback(async (next) => {
    if (!clientId) return;
    try {
      await editorFetch('/api/photo-editor/prefs', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ favouriteFilters: next }),
      });
    } catch (err) {
      console.error('[PhotoEditor] favourites upsert failed:', err.message);
    }
  }, [clientId]);

  // ── Custom filters (Supabase) ────────────────────────────────────────
  const saveCustomFilter = useCallback(async (name, thumbnailDataUrl) => {
    if (!clientId) { toast('Still loading your workspace'); return null; }
    const trimmed = (name || '').trim();
    if (!trimmed) { toast('Give your filter a name'); return null; }
    try {
      const { filter: data } = await editorFetch('/api/photo-editor/filters', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: trimmed, settings: adjustments, thumbnailUrl: thumbnailDataUrl || null }),
      });
      setCustomFilters((prev) => [data, ...prev]);
      setActiveFilter(`custom:${data.id}`);
      // Auto-star the new filter if there's room in favourites (< 5).
      setFavouriteFilters((prev) => {
        if (prev.length >= MAX_FAVOURITES) return prev;
        const next = [...prev, { id: data.id, type: 'custom', name: data.name }];
        persistFavourites(next);
        return next;
      });
      toast(`Saved filter "${trimmed}" ✓`);
      return data;
    } catch (err) {
      console.error('[PhotoEditor] saveCustomFilter failed:', err);
      toast(`Couldn't save filter: ${err.message}`);
      return null;
    }
  }, [clientId, adjustments, persistFavourites, toast]);

  const toggleFavourite = useCallback((ref) => {
    setFavouriteFilters((prev) => {
      const exists = prev.some((f) => f.id === ref.id && f.type === ref.type);
      let next;
      if (exists) {
        next = prev.filter((f) => !(f.id === ref.id && f.type === ref.type));
      } else {
        if (prev.length >= MAX_FAVOURITES) {
          toast('Max 5 favourites — remove one first');
          return prev;
        }
        next = [...prev, ref];
      }
      persistFavourites(next);
      return next;
    });
  }, [persistFavourites, toast]);

  const deleteCustomFilter = useCallback(async (id) => {
    try {
      // Was filtered by id alone; the route is client-scoped, so that gap
      // closes as a side effect of the routing.
      await editorFetch(`/api/photo-editor/filters/${encodeURIComponent(id)}`, { method: 'DELETE' });
      setCustomFilters((prev) => prev.filter((c) => c.id !== id));
      // Drop from favourites too if present.
      setFavouriteFilters((prev) => {
        const next = prev.filter((f) => !(f.type === 'custom' && f.id === id));
        if (next.length !== prev.length) persistFavourites(next);
        return next;
      });
      setActiveFilter((cur) => (cur === `custom:${id}` ? 'Normal' : cur));
      toast('Filter deleted');
    } catch (err) {
      console.error('[PhotoEditor] deleteCustomFilter failed:', err);
      toast(`Couldn't delete: ${err.message}`);
    }
  }, [persistFavourites, toast]);

  // ── Export helpers ───────────────────────────────────────────────────
  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadPhoto = useCallback(async () => {
    if (!selectedPhoto) return;
    setIsSaving(true);
    try {
      const blob = await exportEdited(selectedPhoto.url, adjustments, textLayers, crop);
      triggerDownload(blob, `edited_${Date.now()}.jpg`);
      toast('Downloaded ✓');
    } catch (err) {
      console.error('[PhotoEditor] download failed:', err);
      toast(`Download failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }, [selectedPhoto, adjustments, textLayers, crop, toast]);

  // Phase 6: upload -> getPublicUrl -> row write is now ONE server call, so
  // uploadEdited no longer exists as a separate step. The endpoint also ROLLS
  // BACK the uploaded object if the row write fails — which this path never
  // did, orphaning the rendered image on any DB error, while the library's own
  // upload always had. That asymmetry is closed deliberately (Phase 6
  // decision), not carried across.
  const saveEdited = useCallback(async (blob, { mode, photoId, suffix } = {}) => {
    const form = new FormData();
    form.append('file', blob, 'edited.jpg');
    form.append('mode', mode === 'overwrite' ? 'overwrite' : 'new');
    if (photoId) form.append('photoId', photoId);
    if (suffix)  form.append('suffix', suffix);
    // No client identifier in the form: on a multipart route
    // requireOwnerOrAdmin runs before multer, so ?client= is the only channel
    // that can name a target — and a form field would be the body-beats-query
    // hazard in another costume.
    const { photo } = await editorFetch('/api/brand-photos/edited', { method: 'POST', body: form });
    return photo;
  }, []);

  const saveToLibrary = useCallback(async (saveMode, photo = selectedPhoto) => {
    if (!clientId) { toast('Still loading your workspace — try again'); return null; }
    if (!photo)    { toast('No photo selected'); return null; }
    setIsSaving(true);
    try {
      const blob = await exportEdited(photo.url, adjustments, textLayers, crop);
      const data = await saveEdited(blob, { mode: saveMode, photoId: photo.id });
      toast(saveMode === 'overwrite' ? 'Photo updated ✓' : 'Photo saved to library ✓');
      return data;
    } catch (err) {
      console.error('[PhotoEditor] save failed:', err);
      toast(`Save failed: ${err.message}`);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [clientId, selectedPhoto, adjustments, textLayers, crop, saveEdited, toast]);

  // Batch: render+save a photo with the given settings (no text layers).
  const savePhotoWithSettings = useCallback(async (photo, settings, saveMode) => {
    const blob = await exportEdited(photo.url, settings, []);
    const suffix = Math.random().toString(36).slice(2, 7);
    const saved = await saveEdited(blob, { mode: saveMode, photoId: photo.id, suffix });
    return saved?.url;
  }, [saveEdited]);

  return {
    // state
    selectedPhoto, activeFilter, filterStrength, adjustments,
    textLayers, activeTextLayerId, customFilters, favouriteFilters, isSaving, isDirty,
    crop, cropAspect,
    // selection
    loadPhoto, clearPhoto,
    // crop
    setCrop, setCropAspect, resetCrop,
    // filters
    applyPreset, applyCustomFilter, setStrength,
    saveCustomFilter, deleteCustomFilter, toggleFavourite,
    // adjustments
    updateAdjustment, resetAdjustment, resetAdjustments,
    // text
    addTextLayer, updateTextLayer, removeTextLayer, selectTextLayer,
    // export
    downloadPhoto, saveToLibrary, savePhotoWithSettings,
  };
}
